package pl.decks.personal.pages.ievp.sections.pitch

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.icons.fa.FaPlay
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AnimationDirection
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import pl.decks.personal.components.animations.FlickerAnimation
import pl.decks.personal.components.widgets.revealtext.AnimatedRevealText
import pl.decks.personal.components.widgets.revealtext.RevealSentence
import pl.decks.personal.extensions.animationNotNull
import pl.decks.personal.pages.ievp.IEVPJourney
import pl.decks.personal.pages.ievp.sections.pitch.PitchSectionStylesheet.PersonalIntro
import pl.decks.posthog.client.PostHogClient
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun PitchSectionContent() = Box(
    modifier = PitchSectionStylesheet.Section.toModifier(),
    contentAlignment = Alignment.Center,
) {
    var state by remember { mutableStateOf(PersonalIntroState.FOLDED) }
    Box(
        PersonalIntro.BoundingContainer.toModifier(),
        contentAlignment = Alignment.Center,
    ) {
        PersonalIntroWindow(state, changeState = { state = state.next() })
    }
}

@Composable
private fun PersonalIntroWindow(
    state: PersonalIntroState,
    changeState: () -> Unit,
) = Box(
    modifier = PersonalIntro.Style
        .toModifier(
            if (state == PersonalIntroState.UNFOLDED) PersonalIntro.BoxVariant else PersonalIntro.CircleVariant,
            if (state == PersonalIntroState.FOLDED) PersonalIntro.ActionableVariant else null,
        )
        .thenIf(state == PersonalIntroState.FOLDED) {
            Modifier.cursor(Cursor.Pointer).onClick {
                PostHogClient.capture(IEVPJourney.PitchPlayed)
                changeState()
            }
        }
        .animationNotNull(PersonalIntroStateChangeAnimations.get(state))
        .onAnimationEnd { changeState() },
    contentAlignment = Alignment.Center,
) {
    when (state) {
        PersonalIntroState.FOLDED, PersonalIntroState.DISAPPEARING -> FaPlay(
            modifier = PitchSectionStylesheet.ClickPrompt.toModifier()
                .opacity(if (state == PersonalIntroState.FOLDED) 1f else 0f)
        )

        PersonalIntroState.UNFOLDING -> {}
        PersonalIntroState.UNFOLDED -> PersonalIntroRevealText()
    }
}

@Composable
private fun PersonalIntroRevealText() {
    val cursorFlickerDuration = 700L
    val letterTypeDuration = cursorFlickerDuration / 30
    val sentenceStopDuration = cursorFlickerDuration * 1.5

    AnimatedRevealText(
        sentences = listOf(
            RevealSentence("I'm...", keep = false),
            RevealSentence("If I were to describe...", keep = false),
            RevealSentence("Mathematician by heart and nature.", keep = true),
            RevealSentence("I love...", keep = false),
            RevealSentence("Obsessed with understanding, creating, and transforming.", keep = true),
            RevealSentence("Tech generalist, someone who can make trade-offs in favor of business goals.", keep = true),
            RevealSentence(
                "Data specialist, with a skill set oriented around problem solving, " +
                        "complex systems navigation, opportunity search and change communication.",
                keep = true,
            ),
            RevealSentence(
                "Care to find out what unexpected shortcuts a unique composition of engaged listener, " +
                        "enabled constructor, and compelling storyteller can open?",
                keep = true
            ),
            RevealSentence("That's me.", keep = false),
            RevealSentence("With a few more seconds to spare.", keep = false),
        ),
        letterTypeDuration = letterTypeDuration.milliseconds,
        wordStopDuration = null,
        sentenceStopDuration = sentenceStopDuration.milliseconds,
        trailing = { TerminalCursor(cursorFlickerDuration) },
        modifier = PitchSectionStylesheet.Revealed.Text.toModifier(),
    )
}

@Composable
private fun TerminalCursor(flickerDuration: Long) = SpanText(
    text = "▋",
    modifier = Modifier
        .animation(
            FlickerAnimation.Frames.toAnimation(
                duration = flickerDuration.ms,
                timingFunction = AnimationTimingFunction.StepEnd,
                iterationCount = AnimationIterationCount.Infinite,
                direction = AnimationDirection.Normal,
            ),
        ),
)
